import React, { useEffect, useMemo, useState } from 'react';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import axios from 'axios';
import JWTDecode from 'jwt-decode';
import { useCookies } from 'react-cookie';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Snackbar, TextField, Tooltip, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Container } from '@mui/system';
import { ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import { render } from 'react-dom';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { SerializedError } from '@reduxjs/toolkit';
import debounce from 'lodash.debounce';
import { Decoded } from '../components/Dashboard';
import { ProctorData } from './models';
import {
  Audit,
  RadiusSummary,
  useBatchDeleteRadiusUsersMutation, useGetRadiusDetailsQuery, useListAuditQuery, useListRadiusSummariesQuery, useSearchAuditSummariesQuery, useSearchRadiusSummariesQuery,
} from './query/query';
import SubscriberForm from './AddForm';

interface ApiData {
  items: Array<Audit>
}

const ProctorAuditTable: React.FC = () => {
  const [searchString, setSearchString] = useState('');
  const [searchParameter, setSearchParameter] = useState('id');

  const [showAdd, setShowAdd] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [currentSummary, setCurrentSummary] = useState<RadiusSummary | null>(null);

  const [debouncedSearchString, setDebouncedSearchString] = useState('');

  // Initial list fetch
  const {
    data: initialData,
    refetch: refetchList,
    error: listError, isLoading: listLoading,
  } = useListAuditQuery({ limit: 1000 });

  const [deleteSubs, { isLoading: deleteIsLoading }] = useBatchDeleteRadiusUsersMutation();

  // Search fetch
  const {
    data: searchData, error: searchError,
    isLoading: searchLoading,
  } = useSearchAuditSummariesQuery(
    { searchString: debouncedSearchString, limit: 20, attribute: searchParameter },
    { skip: !debouncedSearchString }, // Skip search if no debounced string
  );

  // Determine rows for the table
  const rows = React.useMemo(() => [...(searchString ? searchData?.items || [] : initialData?.items || [])], [searchData?.items, initialData?.items, listLoading, deleteIsLoading]);
  const renderError = (error: FetchBaseQueryError | SerializedError | undefined) => {
    if (!error) return null;

    if ('data' in error) {
      // Error is FetchBaseQueryError
      return (
        <Typography color="error">
          {
        (error.data as any)?.message || 'API Error occurred'
}
        </Typography>
      );
    }

    // Error is SerializedError
    return (
      <Typography
        color="error"
      >
        {(error as any)?.message || 'An unknown error occurred'}
      </Typography>
    );
  };

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  // Fetch data from the API
  const columns: GridColDef[] = [
    {
      field: 'id', headerName: 'id', width: 200, hideable: true,
    },
    { field: 'createdAt', headerName: 'Timestamp', width: 200 },
    { field: 'user', headerName: 'Audit User', width: 200 },
    { field: 'action', headerName: 'Action', width: 200 },
    { field: 'body', headerName: 'Body', width: 400 },
  ];

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      // Prevent closing on clickaway if desired
      return;
    }
    setSnackMessage('');
  };
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>({});

  // Debounced search handler
  const handleSearchChange = useMemo(
    () => debounce((value: string) => {
      setDebouncedSearchString(value);
    }, 500),
    [],
  );
  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.target.value); // Update immediately for UI feedback
    if (event.target.value === '') {
      // 🔹 If search input is empty, reset back to listing
      refetchList();
    } else {
      handleSearchChange(event.target.value); // Debounce API call
    }
  };

  const handleCheckboxChange = (username: string) => {
    setCheckboxes((prev) => ({
      ...prev,
      [username]: !prev[username],
    }));
  };

  const handleParameterChange = (event: SelectChangeEvent<string>) => {
    setSearchParameter(event.target.value as string);
  };

  return (
    <Box sx={{ paddingTop: 20, paddingLeft: 5, paddingRight: 5 }}>
      {/* Search Input */}
      {!!snackMessage && snackMessage !== '' && <Snackbar onClose={handleClose} message={snackMessage} open={!!snackMessage && snackMessage !== ''} autoHideDuration={3000} />}

      {showAdd && <SubscriberForm summary={currentSummary || undefined} isOpen={showAdd} setIsOpen={setShowAdd} setSnackBarMessage={setSnackMessage} />}
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        {/* Dropdown for search parameters */}
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="search-parameter-label">Search By</InputLabel>
          <Select
            labelId="search-parameter-label"
            value={searchParameter}
            onChange={handleParameterChange}
            label="Search By"
          >
            <MenuItem value="sk">User</MenuItem>
            <MenuItem value="audit_body">Body</MenuItem>
          </Select>
        </FormControl>

        {/* Search Input */}
        <TextField
          label={`Search ${searchParameter}`}
          variant="outlined"
          fullWidth
          value={searchString}
          onChange={onSearchChange}
        />
      </Box>

      {/* Loading Indicator */}
      {(listLoading || searchLoading) && <CircularProgress sx={{ marginBottom: 2 }} />}
      {renderError(listError)}
      {renderError(searchError)}
      <DataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[10, 25, 50]}
        loading={listLoading || searchLoading}
        checkboxSelection
        onRowSelectionModelChange={(newSelection) => {
          setSelectionModel(newSelection);
        }}
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.id}
      />
    </Box>
  );
};
export default ProctorAuditTable;
